/**
 * SLA Monitor list state machine - replaces useSlaMonitorStore (Zustand)
 */
import { setup, assign } from 'xstate'
import type { SlaEvent } from '../../../../entities/sla/model/types'
import type { TriggeredEscalation } from '../../../../entities/sla/model/escalation-engine'

export interface TrackedInteraction {
  interactionId: string
  policyId: string
  createdAt: Date
  queueId: string
  firstResponseAt?: Date
  resolvedAt?: Date
  currentEscalationLevel: number
  slaStatus: 'ok' | 'warning' | 'breached'
  isPaused: boolean
}

export interface SlaMonitorListContext {
  trackedInteractions: Map<string, TrackedInteraction>
  warnings: SlaEvent[]
  breaches: SlaEvent[]
  escalations: TriggeredEscalation[]
  enableWarningNotifications: boolean
  enableBreachNotifications: boolean
  enableEscalationNotifications: boolean
  warningThresholdMinutes: number
  isRunning: boolean
  lastCheckTime: Date | null
}

export type SlaMonitorListEvent =
  | {
      type: 'TRACK_INTERACTION'
      interactionId: string
      policyId: string
      queueId: string
      createdAt: Date
      firstResponseAt?: Date
      resolvedAt?: Date
    }
  | { type: 'UNTRACK_INTERACTION'; interactionId: string }
  | {
      type: 'UPDATE_INTERACTION_STATUS'
      interactionId: string
      updates: Partial<TrackedInteraction>
    }
  | { type: 'PAUSE_TRACKING'; interactionId: string }
  | { type: 'RESUME_TRACKING'; interactionId: string }
  | { type: 'ADD_WARNING'; event: SlaEvent }
  | { type: 'ADD_BREACH'; event: SlaEvent }
  | { type: 'ADD_ESCALATION'; escalation: TriggeredEscalation }
  | { type: 'CLEAR_EVENTS' }
  | { type: 'SET_WARNING_NOTIFICATIONS'; enabled: boolean }
  | { type: 'SET_BREACH_NOTIFICATIONS'; enabled: boolean }
  | { type: 'SET_ESCALATION_NOTIFICATIONS'; enabled: boolean }
  | { type: 'SET_WARNING_THRESHOLD'; minutes: number }
  | { type: 'START_MONITORING' }
  | { type: 'STOP_MONITORING' }
  | { type: 'SET_LAST_CHECK_TIME'; time: Date }
  | { type: 'CLEAR' }

const MAX_EVENTS = 100

const machineSetup = setup({
  types: {
    context: {} as SlaMonitorListContext,
    events: {} as SlaMonitorListEvent,
  },
})

export const slaMonitorListMachine = machineSetup.createMachine({
  id: 'slaMonitorList',
  context: {
    trackedInteractions: new Map(),
    warnings: [],
    breaches: [],
    escalations: [],
    enableWarningNotifications: true,
    enableBreachNotifications: true,
    enableEscalationNotifications: true,
    warningThresholdMinutes: 5,
    isRunning: false,
    lastCheckTime: null,
  },
  on: {
    TRACK_INTERACTION: {
      actions: assign({
        trackedInteractions: ({ context, event }) => {
          if (event.type !== 'TRACK_INTERACTION') return context.trackedInteractions
          const next = new Map(context.trackedInteractions)
          next.set(event.interactionId, {
            interactionId: event.interactionId,
            policyId: event.policyId,
            queueId: event.queueId,
            createdAt: event.createdAt,
            firstResponseAt: event.firstResponseAt,
            resolvedAt: event.resolvedAt,
            currentEscalationLevel: 0,
            slaStatus: 'ok',
            isPaused: false,
          })
          return next
        },
      }),
    },
    UNTRACK_INTERACTION: {
      actions: assign({
        trackedInteractions: ({ context, event }) => {
          if (event.type !== 'UNTRACK_INTERACTION') return context.trackedInteractions
          const next = new Map(context.trackedInteractions)
          next.delete(event.interactionId)
          return next
        },
      }),
    },
    UPDATE_INTERACTION_STATUS: {
      actions: assign({
        trackedInteractions: ({ context, event }) => {
          if (event.type !== 'UPDATE_INTERACTION_STATUS') return context.trackedInteractions
          const tracked = context.trackedInteractions.get(event.interactionId)
          if (!tracked) return context.trackedInteractions
          const next = new Map(context.trackedInteractions)
          next.set(event.interactionId, { ...tracked, ...event.updates })
          return next
        },
      }),
    },
    PAUSE_TRACKING: {
      actions: assign({
        trackedInteractions: ({ context, event }) => {
          if (event.type !== 'PAUSE_TRACKING') return context.trackedInteractions
          const tracked = context.trackedInteractions.get(event.interactionId)
          if (!tracked) return context.trackedInteractions
          const next = new Map(context.trackedInteractions)
          next.set(event.interactionId, { ...tracked, isPaused: true })
          return next
        },
      }),
    },
    RESUME_TRACKING: {
      actions: assign({
        trackedInteractions: ({ context, event }) => {
          if (event.type !== 'RESUME_TRACKING') return context.trackedInteractions
          const tracked = context.trackedInteractions.get(event.interactionId)
          if (!tracked) return context.trackedInteractions
          const next = new Map(context.trackedInteractions)
          next.set(event.interactionId, { ...tracked, isPaused: false })
          return next
        },
      }),
    },
    ADD_WARNING: {
      actions: assign({
        warnings: ({ context, event }) =>
          event.type === 'ADD_WARNING'
            ? [event.event, ...context.warnings].slice(0, MAX_EVENTS)
            : context.warnings,
      }),
    },
    ADD_BREACH: {
      actions: assign({
        breaches: ({ context, event }) =>
          event.type === 'ADD_BREACH'
            ? [event.event, ...context.breaches].slice(0, MAX_EVENTS)
            : context.breaches,
      }),
    },
    ADD_ESCALATION: {
      actions: assign({
        escalations: ({ context, event }) =>
          event.type === 'ADD_ESCALATION'
            ? [event.escalation, ...context.escalations].slice(0, MAX_EVENTS)
            : context.escalations,
      }),
    },
    CLEAR_EVENTS: {
      actions: assign({ warnings: [], breaches: [], escalations: [] }),
    },
    SET_WARNING_NOTIFICATIONS: {
      actions: assign({
        enableWarningNotifications: ({ event }) =>
          event.type === 'SET_WARNING_NOTIFICATIONS' ? event.enabled : true,
      }),
    },
    SET_BREACH_NOTIFICATIONS: {
      actions: assign({
        enableBreachNotifications: ({ event }) =>
          event.type === 'SET_BREACH_NOTIFICATIONS' ? event.enabled : true,
      }),
    },
    SET_ESCALATION_NOTIFICATIONS: {
      actions: assign({
        enableEscalationNotifications: ({ event }) =>
          event.type === 'SET_ESCALATION_NOTIFICATIONS' ? event.enabled : true,
      }),
    },
    SET_WARNING_THRESHOLD: {
      actions: assign({
        warningThresholdMinutes: ({ event }) =>
          event.type === 'SET_WARNING_THRESHOLD' ? event.minutes : 5,
      }),
    },
    START_MONITORING: {
      actions: assign({ isRunning: true }),
    },
    STOP_MONITORING: {
      actions: assign({ isRunning: false }),
    },
    SET_LAST_CHECK_TIME: {
      actions: assign({
        lastCheckTime: ({ event }) => (event.type === 'SET_LAST_CHECK_TIME' ? event.time : null),
      }),
    },
    CLEAR: {
      actions: assign({
        trackedInteractions: new Map(),
        warnings: [],
        breaches: [],
        escalations: [],
      }),
    },
  },
})
